// src/pages/RolesPermissions.js
import React, { useEffect, useState, useCallback } from "react";
import {
  Container,
  PageHeader,
  PageTitle,
  PageSubtitle,
  TabRow,
  RoleTab,
  RoleTabWrapper,
  TabIconBtn,
  AddRoleBtn,
  PanelRow,
  Panel,
  PanelTitle,
  RoleBadge,
  SearchBox,
  CategoryBlock,
  CategoryHeader,
  CategoryItems,
  PermissionRow,
  PermissionInfo,
  PermissionName,
  PermissionDesc,
  AddIcon,
  AddAllBtn,
  ArrowCol,
  ArrowBtn,
  SelectedList,
  SelectedCard,
  SelectedCardInfo,
  SelectedCardName,
  SelectedCardDesc,
  ScopeGroup,
  ScopeBtn,
  DeleteIconBtn,
  DiscardBtn,
  SaveBtn,
  SaveSuccessText,
  EmptyState,
  AvailableScroll,
  ErrorBanner,
  ModalOverlay,
  ModalBox,
  ModalTitle,
  ModalSubtitle,
  ModalEmoji,
  ModalFooter,
  ModalCancelBtn,
  ModalConfirmBtn,
  FieldGroup,
  FieldLabel,
  FieldInput,
  FieldTextarea,
  FieldError,
} from "../style-comps/RolesPermissions.styles";

import {
  getRoles,
  getAllPermissions,
  getRolePermissions,
  getRoleUsers,
  deleteRole,
  createRole,
  updateRole,
  addRolePermission,
  removeRolePermission,
  updateRolePermissionScope,
} from '../services/api';


// ─── Helpers ──────────────────────────────────────────────────────────────────
// Group permissions by their prefix (e.g. "Calendar.Read" → "Calendar")
const groupByCategory = (permissions) => {
  const map = {};
  permissions.forEach((p) => {
    const name = p.Name ?? p.name ?? "";
    const cat = name.includes(".") ? name.split(".")[0] : "General";
    if (!map[cat]) map[cat] = [];
    map[cat].push(p);
  });
  return map;
};

const scopeLabel = (scope) => {
  if (scope === 2 || scope === "All") return "All";
  if (scope === 1 || scope === "Own") return "Own";
  return "None";
};

// ─── Icons ────────────────────────────────────────────────────────────────────
const ChevronRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const LockIcon = ({ size = 11 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [allPermissions, setAllPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [openCategories, setOpenCategories] = useState({});
  const [permSearch, setPermSearch] = useState("");
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingPerms, setLoadingPerms] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [savedPermissions, setSavedPermissions] = useState([]);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [showAddRoleModal, setShowAddRoleModal] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [creatingRole, setCreatingRole] = useState(false);
  const [createRoleError, setCreateRoleError] = useState("");
  const [hoveredRoleId, setHoveredRoleId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteBlocked, setDeleteBlocked] = useState(null);   // { role, users }：Role 綁有使用者，禁止刪除
  const [checkingDeleteRoleId, setCheckingDeleteRoleId] = useState(null);
  const [editRoleModal, setEditRoleModal] = useState(null);
  const [editRoleName, setEditRoleName] = useState("");
  const [editRoleDesc, setEditRoleDesc] = useState("");
  const [updatingRole, setUpdatingRole] = useState(false);
  const [editRoleError, setEditRoleError] = useState("");
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  // ── 權限治理：Admin / SuperAdmin 角色僅 SuperAdmin 本人可管理 ──
  const currentUserRoles = JSON.parse(localStorage.getItem('roles') || '[]');
  const isSuperAdminUser = currentUserRoles.some((r) => r.toLowerCase() === 'superadmin');
  const PROTECTED_ROLE_NAMES = ['admin', 'superadmin'];
  const isRoleLocked = (r) =>
    !isSuperAdminUser &&
    PROTECTED_ROLE_NAMES.includes((r?.Name ?? r?.name ?? '').toLowerCase());
  const selectedRoleLocked = isRoleLocked(selectedRole);

  // System 類別的 Permissions（System.xxx）也僅 SuperAdmin 可調整
  const isSystemPermissionLocked = (permName) => {
    if (isSuperAdminUser) return false;
    return (permName ?? '').split('.')[0].toLowerCase() === 'system';
  };

  // ── Load all roles on mount ────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingRoles(true);
        const data = await getRoles();
        const arr = Array.isArray(data) ? data : data?.data ?? [];
        setRoles(arr);
        if (arr.length > 0) setSelectedRole(arr[0]);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoadingRoles(false);
      }
    };
    load();
  }, []);

  // ── Load all permissions on mount ──────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getAllPermissions();
        const arr = Array.isArray(data) ? data : data?.data ?? [];
        setAllPermissions(arr);
        // default: open the first category
        const grouped = groupByCategory(arr);
        const firstCat = Object.keys(grouped)[0];
        if (firstCat) setOpenCategories({ [firstCat]: true });
      } catch (e) {
        console.error("❌ fetchAllPermissions:", e.message);
      }
    };
    load();
  }, []);

  // ── Load role permissions when selected role changes ───────
  useEffect(() => {
    if (!selectedRole) return;
    const load = async () => {
      try {
        setLoadingPerms(true);
        const roleId = selectedRole.Id ?? selectedRole.id;
        const data = await getRolePermissions(roleId);
        const arr = Array.isArray(data) ? data : data?.data ?? [];
        setRolePermissions(arr);
        setSavedPermissions(arr);   // 記錄原始狀態
        setIsDirty(false);          // 重置髒狀態
      } catch (e) {
        console.error("❌ fetchRolePermissions:", e.message);
        setRolePermissions([]);
        setSavedPermissions([]);
      } finally {
        setLoadingPerms(false);
      }
    };
    load();
  }, [selectedRole]);

  // ── Helpers ───────────────────────────────────────────────
  const getRolePermName = (rp) =>
    rp.PermissionName ?? rp.permissionName ?? rp.Name ?? rp.name ?? "";

  const isAlreadySelected = useCallback(
    (permName) =>
      rolePermissions.some(
        (rp) => getRolePermName(rp).toLowerCase() === permName.toLowerCase()
      ),
    [rolePermissions]
  );

  const getScopeOfSelected = (permName) => {
    const found = rolePermissions.find(
      (rp) => getRolePermName(rp).toLowerCase() === permName.toLowerCase()
    );
    if (!found) return "Own";
    return scopeLabel(found.Scope ?? found.scope ?? 1);
  };

  // ── Add permission (local only, no API) ──────────────────
  const handleAdd = (perm) => {
    if (!selectedRole || selectedRoleLocked) return;
    const permName = perm.Name ?? perm.name;
    if (isSystemPermissionLocked(permName)) return;
    if (isAlreadySelected(permName)) return;
    setRolePermissions((prev) => [
      ...prev,
      { PermissionName: permName, Scope: 1, Description: perm.Description ?? perm.description ?? "" },
    ]);
    setIsDirty(true);
  };

  const handleAddAll = () => {
    if (!selectedRole || selectedRoleLocked) return;

    const newPermissions = filtered
      .filter((p) => {
        const permName = p.Name ?? p.name;
        return !isAlreadySelected(permName) && !isSystemPermissionLocked(permName);
      })
      .map((p) => ({
        PermissionName: p.Name ?? p.name,
        Scope: 1,
        Description: p.Description ?? p.description ?? "",
      }));

    if (newPermissions.length === 0) return;

    setRolePermissions((prev) => [...prev, ...newPermissions]);
    setIsDirty(true);
  };

  // ── Remove permission (local only, no API) ───────────────
  const handleRemove = (permName) => {
    if (!selectedRole || selectedRoleLocked) return;
    if (isSystemPermissionLocked(permName)) return;
    setRolePermissions((prev) =>
      prev.filter((rp) => getRolePermName(rp).toLowerCase() !== permName.toLowerCase())
    );
    setIsDirty(true);
  };

  // ── Update scope (local only, no API) ────────────────────
  const handleScopeChange = (permName, newScope) => {
    if (!selectedRole || selectedRoleLocked) return;
    if (isSystemPermissionLocked(permName)) return;
    setRolePermissions((prev) =>
      prev.map((rp) =>
        getRolePermName(rp).toLowerCase() === permName.toLowerCase()
          ? { ...rp, Scope: newScope === "All" ? 2 : newScope === "Own" ? 1 : 0 }
          : rp
      )
    );
    setIsDirty(true);
  };

  // ── Discard changes ───────────────────────────────────────
  const handleDiscard = () => {
    setRolePermissions(savedPermissions);
    setIsDirty(false);
  };

  // ── Save: diff old vs new, call batch add + delete ────────
  const handleSave = () => {
    if (!selectedRole || selectedRoleLocked) return;
    setShowSaveConfirm(true);
  };

  const handleConfirmSave = async () => {
    setShowSaveConfirm(false);

    const roleId = selectedRole.Id ?? selectedRole.id;
    const scopeMap = { All: 2, Own: 1, None: 0 };

    // 計算要新增 / 更新的（目前有，原本沒有 or scope 不同）
    const toAddOrUpdate = rolePermissions.filter((rp) => {
      const name = getRolePermName(rp);
      const original = savedPermissions.find(
        (s) => getRolePermName(s).toLowerCase() === name.toLowerCase()
      );
      if (!original) return true; // 新增
      const newScope = rp.Scope ?? 2;
      const oldScope = original.Scope ?? 2;
      return newScope !== oldScope; // scope 有變
    });

    // 計算要刪除的（原本有，現在沒有）
    const toDelete = savedPermissions.filter((s) => {
      const name = getRolePermName(s);
      return !rolePermissions.some(
        (rp) => getRolePermName(rp).toLowerCase() === name.toLowerCase()
      );
    });

    try {
      setSaving(true);

      // batch 新增 / 更新 (一次送全部)
      if (toAddOrUpdate.length > 0) {
        for (const rp of toAddOrUpdate) {
          await addRolePermission(
            roleId,
            getRolePermName(rp),
            rp.Scope === 1 ? "Own" : rp.Scope === 0 ? "None" : "All"
          );
        }
      }

      // 逐筆刪除
      for (const rp of toDelete) {
        const name = getRolePermName(rp);
        await removeRolePermission(roleId, name);
      }

      // 更新 savedPermissions 為目前狀態
      setSavedPermissions(rolePermissions);
      setIsDirty(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (e) {
      setError(`Failed to save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  // ── Create new Role ──────────────────────────────────────
  const handleCreateRole = async () => {
    if (!newRoleName.trim()) {
      setCreateRoleError("Role name is required.");
      return;
    }
    try {
      setCreatingRole(true);
      setCreateRoleError("");
      const created = await createRole(newRoleName.trim(), newRoleDesc.trim());
      const data = await getRoles();
      const arr = Array.isArray(data) ? data : data?.data ?? [];
      setRoles(arr);
      const newRole = arr.find(
        (r) => (r.Id ?? r.id) === (created.Id ?? created.id)
      ) ?? arr[arr.length - 1];
      setSelectedRole(newRole);
      setShowAddRoleModal(false);
      setNewRoleName("");
      setNewRoleDesc("");
    } catch (e) {
      setCreateRoleError(e.message);
    } finally {
      setCreatingRole(false);
    }
  };

  // ── Edit Role ─────────────────────────────────────────────
  const handleOpenEditRole = (r) => {
    if (isRoleLocked(r)) return;
    setEditRoleModal(r);
    setEditRoleName(r.Name ?? r.name ?? "");
    setEditRoleDesc(r.Description ?? r.description ?? "");
    setEditRoleError("");
  };

  const handleUpdateRole = async () => {
    if (!editRoleName.trim()) {
      setEditRoleError("Role name is required.");
      return;
    }
    try {
      setUpdatingRole(true);
      setEditRoleError("");
      const roleId = editRoleModal.Id ?? editRoleModal.id;
      await updateRole(roleId, editRoleName.trim(), editRoleDesc.trim());
      setRoles((prev) =>
        prev.map((r) =>
          (r.Id ?? r.id) === roleId
            ? { ...r, Name: editRoleName.trim(), Description: editRoleDesc.trim() }
            : r
        )
      );
      if ((selectedRole?.Id ?? selectedRole?.id) === roleId) {
        setSelectedRole((prev) => ({
          ...prev,
          Name: editRoleName.trim(),
          Description: editRoleDesc.trim(),
        }));
      }
      setEditRoleModal(null);
    } catch (e) {
      setEditRoleError(e.message);
    } finally {
      setUpdatingRole(false);
    }
  };

  // ── Delete Role：點擊刪除時先檢查有沒有使用者綁定 ─────────
  const handleDeleteClick = async (r) => {
    if (isRoleLocked(r)) return;
    const roleId = r.Id ?? r.id;
    try {
      setCheckingDeleteRoleId(roleId);
      const data = await getRoleUsers(roleId);
      const boundUsers = Array.isArray(data) ? data : data?.data ?? [];

      if (boundUsers.length > 0) {
        setDeleteBlocked({ role: r, users: boundUsers });   // 有綁定 → 擋下並列出使用者
      } else {
        setDeleteConfirm(r);                                // 沒綁定 → 進原本的確認刪除流程
      }
    } catch (e) {
      setError(`Unable to check role assignments: ${e.message}`);
    } finally {
      setCheckingDeleteRoleId(null);
    }
  };

  // ── Delete Role ──────────────────────────────────────────
  const handleDeleteRole = async () => {
    if (!deleteConfirm) return;
    const roleId = deleteConfirm.Id ?? deleteConfirm.id;
    try {
      await deleteRole(roleId);
      const updated = roles.filter((r) => (r.Id ?? r.id) !== roleId);
      setRoles(updated);
      if ((selectedRole?.Id ?? selectedRole?.id) === roleId) {
        setSelectedRole(updated[0] ?? null);
      }
      setDeleteConfirm(null);
    } catch (e) {
      setError(`Delete failed: ${e.message}`);
      setDeleteConfirm(null);
    }
  };

  // ── Toggle category accordion ─────────────────────────────
  const toggleCategory = (cat) => {
    setOpenCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  // ── Filtered + grouped available permissions ───────────────
  const filtered = allPermissions.filter((p) => {
    const name = (p.Name ?? p.name ?? "").toLowerCase();
    const desc = (p.Description ?? p.description ?? "").toLowerCase();
    const q = permSearch.toLowerCase();
    return name.includes(q) || desc.includes(q);
  });
  const grouped = groupByCategory(filtered);

  // ── Desc lookup for selected perms ────────────────────────
  const getDesc = (permName) => {
    const found = allPermissions.find(
      (p) => (p.Name ?? p.name ?? "").toLowerCase() === permName.toLowerCase()
    );
    return found?.Description ?? found?.description ?? "";
  };

  // ─────────────────────────────────────────────────────────
  return (
    <Container>
      <PageHeader>
        <PageTitle>Roles &amp; Permissions</PageTitle>
        <PageSubtitle>
          Configure and audit access levels for the KozyGuru Zen ecosystem.
          Maintain editorial security through precise permission management.
        </PageSubtitle>
      </PageHeader>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {/* Role Tabs */}
      <TabRow>
        {loadingRoles ? (
          <EmptyState>Loading roles…</EmptyState>
        ) : (
          roles.map((r) => {
            const id = r.Id ?? r.id;
            const name = r.Name ?? r.name;
            const isActive = (selectedRole?.Id ?? selectedRole?.id) === id;
            const isHovered = hoveredRoleId === id;
            return (
              <RoleTabWrapper
                key={id}
                onMouseEnter={() => setHoveredRoleId(id)}
                onMouseLeave={() => setHoveredRoleId(null)}
              >
                <RoleTab
                  active={isActive ? 1 : 0}
                  onClick={() => setSelectedRole(r)}
                >
                  {name}{isRoleLocked(r) && <span style={{ marginLeft: 4, display: "inline-flex", verticalAlign: "middle" }} title="Only SuperAdmin can manage this role"><LockIcon /></span>}
                </RoleTab>
                {isHovered && !isRoleLocked(r) && (
                  <TabIconBtn
                    onClick={(e) => { e.stopPropagation(); handleOpenEditRole(r); }}
                    title="Edit this role"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                      <path d="m15 5 4 4" />
                    </svg>
                  </TabIconBtn>
                )}
                {isHovered && !isRoleLocked(r) && (
                  <TabIconBtn
                    danger={1}
                    onClick={(e) => { e.stopPropagation(); handleDeleteClick(r); }}
                    title="Delete this role"
                    disabled={checkingDeleteRoleId === id}
                  >
                    {checkingDeleteRoleId === id ? '…' : '✕'}
                  </TabIconBtn>
                )}
              </RoleTabWrapper>
            );
          })
        )}
        <AddRoleBtn onClick={() => { setShowAddRoleModal(true); setCreateRoleError(""); }}>
          <span style={{ fontSize: 18, lineHeight: 1 }}>+</span> Add Role
        </AddRoleBtn>
      </TabRow>

      {/* Main Panels */}
      <PanelRow>
        {/* ── Left: Available Permissions ── */}
        <Panel>
          <PanelTitle>
            <span>Available Permissions</span>
            <AddAllBtn onClick={handleAddAll} disabled={!selectedRole || selectedRoleLocked}>
              Add All
            </AddAllBtn>
          </PanelTitle>

          <SearchBox>
            <SearchIcon />
            <input
              placeholder="Filter permissions..."
              value={permSearch}
              onChange={(e) => setPermSearch(e.target.value)}
            />
          </SearchBox>

          <AvailableScroll>
            {Object.keys(grouped).length === 0 ? (
              <EmptyState>No permissions found.</EmptyState>
            ) : (
              Object.entries(grouped).map(([cat, perms]) => (
                <CategoryBlock key={cat}>
                  <CategoryHeader
                    open={openCategories[cat] ? 1 : 0}
                    onClick={() => toggleCategory(cat)}
                  >
                    <span>{cat}</span>
                    <ChevronRight />
                  </CategoryHeader>
                  <CategoryItems open={openCategories[cat] ? 1 : 0}>
                    {perms.map((p) => {
                      const pName = p.Name ?? p.name;
                      const pDesc = p.Description ?? p.description ?? "";
                      const already = isAlreadySelected(pName);
                      const permLocked = isSystemPermissionLocked(pName);
                      return (
                        <PermissionRow key={pName}>
                          <PermissionInfo>
                            <PermissionName>
                              {pName}
                              {permLocked && <span style={{ marginLeft: 5, display: "inline-flex", verticalAlign: "middle", color: "#999" }} title="Only SuperAdmin can manage System permissions"><LockIcon /></span>}
                            </PermissionName>
                            {pDesc && <PermissionDesc>{pDesc}</PermissionDesc>}
                          </PermissionInfo>
                          <AddIcon
                            title={permLocked ? "Only SuperAdmin can manage System permissions" : selectedRoleLocked ? "Only SuperAdmin can manage this role" : already ? "Already added" : "Add permission"}
                            disabled={already || selectedRoleLocked || permLocked}
                            onClick={() => handleAdd(p)}
                          >
                            +
                          </AddIcon>
                        </PermissionRow>
                      );
                    })}
                  </CategoryItems>
                </CategoryBlock>
              ))
            )}
          </AvailableScroll>
        </Panel>

        {/* ── Middle: Arrow buttons ── */}
        <ArrowCol>
          {/* <ArrowBtn fwd={1} title="Add selected">›</ArrowBtn>
          <ArrowBtn title="Remove selected">‹</ArrowBtn> */}
        </ArrowCol>

        {/* ── Right: Selected Permissions ── */}
        <Panel>
          <PanelTitle>
            <span>Selected Permissions</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {selectedRole && (
                <RoleBadge>
                  {selectedRole.Name ?? selectedRole.name} Role
                </RoleBadge>
              )}
              {selectedRoleLocked && (
                <span style={{ fontSize: 12, color: "#999", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <LockIcon /> View only — managed by SuperAdmin
                </span>
              )}
              {isDirty && (
                <>
                  <DiscardBtn onClick={handleDiscard}>Discard</DiscardBtn>
                  <SaveBtn onClick={handleSave} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </SaveBtn>
                </>
              )}
              {saveSuccess && <SaveSuccessText>Saved!</SaveSuccessText>}
            </div>
          </PanelTitle>

          {loadingPerms ? (
            <EmptyState>Loading permissions…</EmptyState>
          ) : rolePermissions.length === 0 ? (
            <EmptyState>No permissions assigned to this role yet.</EmptyState>
          ) : (
            <SelectedList>
              {rolePermissions.map((rp) => {
                const pName = getRolePermName(rp);
                const scope = getScopeOfSelected(pName);
                const desc =
                  rp.Description ?? rp.description ?? getDesc(pName);
                const permLocked = isSystemPermissionLocked(pName);
                return (
                  <SelectedCard key={pName}>
                    <SelectedCardInfo>
                      <SelectedCardName>
                        {pName}
                        {permLocked && <span style={{ marginLeft: 5, display: "inline-flex", verticalAlign: "middle", color: "#999" }} title="Only SuperAdmin can manage System permissions"><LockIcon /></span>}
                      </SelectedCardName>
                      {desc && <SelectedCardDesc>{desc}</SelectedCardDesc>}
                    </SelectedCardInfo>
                    <ScopeGroup>
                      {["All", "Own", "None"].map((s) => (
                        <ScopeBtn
                          key={s}
                          active={scope === s ? 1 : 0}
                          disabled={selectedRoleLocked || permLocked}
                          onClick={() => handleScopeChange(pName, s)}
                        >
                          {s}
                        </ScopeBtn>
                      ))}
                    </ScopeGroup>
                    {!selectedRoleLocked && !permLocked && (
                      <DeleteIconBtn
                        title="Remove permission"
                        onClick={() => handleRemove(pName)}
                      >
                        <TrashIcon />
                      </DeleteIconBtn>
                    )}
                  </SelectedCard>
                );
              })}
            </SelectedList>
          )}
        </Panel>
      </PanelRow>

      {/* ── Edit Role Modal ── */}
      {editRoleModal && (
        <ModalOverlay>
          <ModalBox>
            <ModalTitle>Edit Role</ModalTitle>
            <ModalSubtitle>修改「{editRoleModal.Name ?? editRoleModal.name}」的名稱與描述。</ModalSubtitle>
            <FieldGroup>
              <FieldLabel>Role Name <span style={{ color: "crimson" }}>*</span></FieldLabel>
              <FieldInput
                value={editRoleName}
                onChange={(e) => setEditRoleName(e.target.value)}
                placeholder="e.g. Manager"
              />
            </FieldGroup>
            <FieldGroup last={1}>
              <FieldLabel>Description</FieldLabel>
              <FieldTextarea
                value={editRoleDesc}
                onChange={(e) => setEditRoleDesc(e.target.value)}
                placeholder="描述這個 Role 的用途..."
                rows={3}
              />
            </FieldGroup>
            {editRoleError && <FieldError>{editRoleError}</FieldError>}
            <ModalFooter>
              <ModalCancelBtn onClick={() => setEditRoleModal(null)}>Cancel</ModalCancelBtn>
              <ModalConfirmBtn onClick={handleUpdateRole} disabled={updatingRole}>
                {updatingRole ? "Saving…" : "Save Changes"}
              </ModalConfirmBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Delete Role Blocked Modal（Role 綁有使用者，不可刪除） ── */}
      {deleteBlocked && (
        <ModalOverlay>
          <ModalBox width="440px">
            <ModalEmoji>🚫</ModalEmoji>
            <ModalTitle center={1}>
              Cannot Delete Role: {deleteBlocked.role.Name ?? deleteBlocked.role.name}
            </ModalTitle>
            <ModalSubtitle center={1}>
              This role is currently assigned to <strong style={{ color: "crimson" }}>{deleteBlocked.users.length}</strong> user{deleteBlocked.users.length > 1 ? "s" : ""}.
              Remove this role from the following user{deleteBlocked.users.length > 1 ? "s" : ""} in User Setting before deleting it:
            </ModalSubtitle>
            <div style={{
              maxHeight: 180,
              overflowY: "auto",
              margin: "12px 0",
              padding: "10px 14px",
              background: "#f9fafb",
              border: "1px solid #eee",
              borderRadius: 8,
              textAlign: "left",
            }}>
              {deleteBlocked.users.map((u) => (
                <div key={u.UserId} style={{ fontSize: 13, padding: "3px 0", color: "#333" }}>
                  {u.UserName ?? `User ${u.UserId}`}
                  <span style={{ color: "#999", marginLeft: 6 }}>(ID: {u.UserId})</span>
                </div>
              ))}
            </div>
            <ModalFooter center={1}>
              <ModalCancelBtn onClick={() => setDeleteBlocked(null)}>Got it</ModalCancelBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Delete Role Confirm Modal ── */}
      {deleteConfirm && (
        <ModalOverlay>
          <ModalBox width="400px">
            <ModalEmoji>⚠️</ModalEmoji>
            <ModalTitle center={1}>Delete Role: {deleteConfirm.Name ?? deleteConfirm.name}</ModalTitle>
            <ModalSubtitle center={1}>
              This action is <strong style={{ color: "crimson" }}>irreversible</strong>. All permission settings under this role will also be removed. Are you sure you want to continue?
            </ModalSubtitle>
            <ModalFooter center={1}>
              <ModalCancelBtn onClick={() => setDeleteConfirm(null)}>Cancel</ModalCancelBtn>
              <ModalConfirmBtn danger={1} onClick={handleDeleteRole}>Delete</ModalConfirmBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Save Confirm Modal ── */}
      {showSaveConfirm && (
        <ModalOverlay>
          <ModalBox width="400px">
            <ModalEmoji>💾</ModalEmoji>
            <ModalTitle center={1}>Save Permission Settings</ModalTitle>
            <ModalSubtitle center={1}>
              Are you sure you want to save these permission settings for{" "}
              <strong style={{ color: "#333" }}>{selectedRole?.Name ?? selectedRole?.name}</strong>?
            </ModalSubtitle>
            <ModalFooter center={1}>
              <ModalCancelBtn onClick={() => setShowSaveConfirm(false)}>Cancel</ModalCancelBtn>
              <ModalConfirmBtn onClick={handleConfirmSave}>Confirm Save</ModalConfirmBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Add Role Modal ── */}
      {showAddRoleModal && (
        <ModalOverlay>
          <ModalBox>
            <ModalTitle>Add New Role</ModalTitle>
            <ModalSubtitle>建立一個新的角色，之後可以指定對應的 Permissions。</ModalSubtitle>
            <FieldGroup>
              <FieldLabel>Role Name <span style={{ color: "crimson" }}>*</span></FieldLabel>
              <FieldInput
                value={newRoleName}
                onChange={(e) => setNewRoleName(e.target.value)}
                placeholder="e.g. Manager"
              />
            </FieldGroup>
            <FieldGroup last={1}>
              <FieldLabel>Description</FieldLabel>
              <FieldTextarea
                value={newRoleDesc}
                onChange={(e) => setNewRoleDesc(e.target.value)}
                placeholder="描述這個 Role 的用途..."
                rows={3}
              />
            </FieldGroup>
            {createRoleError && <FieldError>{createRoleError}</FieldError>}
            <ModalFooter>
              <ModalCancelBtn onClick={() => { setShowAddRoleModal(false); setNewRoleName(""); setNewRoleDesc(""); setCreateRoleError(""); }}>
                Cancel
              </ModalCancelBtn>
              <ModalConfirmBtn onClick={handleCreateRole} disabled={creatingRole}>
                {creatingRole ? "Creating…" : "Create Role"}
              </ModalConfirmBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default RolesPermissions;